import React, { useEffect, useState } from "react";
import nom35AnalyticsApi from "../../api/nom35AnalyticsApi";
import ageRanges from "../../data/ageRanges";
import biologicalSexList from "../../data/biologicalSex";
import nom035Ranges from "../../data/nom035Ranges";
import Nom035SummaryChart from "../../components/analytics/Nom035SummaryChart";
import styles from "./Nom035AnalyticsPage.module.css";

const categoryKeys = [
  "environment",
  "factors",
  "timeOrganization",
  "leadershipRelations",
  "organizationalEnvironment"
];

const categoryLabels = {
  environment: "Ambiente de trabajo",
  factors: "Factores de la actividad",
  timeOrganization: "Organización del tiempo",
  leadershipRelations: "Liderazgo y relaciones",
  organizationalEnvironment: "Entorno organizacional"
};

const domainLabels = {
  conditions: "Condiciones ambientales",
  workload: "Carga de trabajo",
  control: "Falta de control",
  workday: "Jornada de trabajo",
  interference: "Interferencia en el trabajo",
  leadership: "Liderazgo",
  relationships: "Relaciones en el trabajo",
  violence: "Violencia",
  recognition: "Reconocimiento",
  belonging: "Sentido de pertenencia"
};

const levelOrder = ["Nulo","Bajo","Medio","Alto","Muy alto"];
const globalRangeLabels = levelOrder.reduce((acc, lvl, i) => {
  acc[lvl] = nom035Ranges.global.range[i];
  return acc;
}, {});
const categoryRangeLabels = {};
const domainRangeLabels = {};
Object.entries(nom035Ranges.categoryDomains).forEach(
  ([catKey, { range, domains }]) => {
    categoryRangeLabels[catKey] = levelOrder.reduce((acc, lvl, i) => {
      acc[lvl] = range[i];
      return acc;
    }, {});
    Object.entries(domains).forEach(([domKey, { range: domRange }]) => {
      domainRangeLabels[domKey] = levelOrder.reduce((acc, lvl, i) => {
        acc[lvl] = domRange[i];
        return acc;
      }, {});
    });
  }
);


export default function Nom035GlobalAnalyticsPage() {
  const [totalResponses, setTotalResponses] = useState(null);

  const filterConfigs = [
    { name: "ageRange",       label: "Rango de edad",       options: ageRanges },
    { name: "biologicalSex",  label: "Sexo biológico",       options: biologicalSexList }
  ];

  useEffect(() => {
    (async () => {
      try {
        const { totalResponses } = await nom35AnalyticsApi.getResponsesCountGlobal();
        setTotalResponses(totalResponses);
      } catch (err) {
        console.error("Error obteniendo total global de encuestados:", err);
      }
    })();
  }, []);

  return (
  <div className={styles.page}>
    <header className={styles.header}>
      <h2 className={styles.title}>Analítica NOM-035</h2>
     
      {totalResponses !== null && (
        <div className={styles.totalResponses}>
          <span className={styles.totalLabel}>Total de encuestados:</span>
          <span className={styles.totalCount}>{totalResponses}</span>
        </div>
      )}
    </header>

    {/* Resumen general */}
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Resumen general</h3>
      <div className={styles.domainWrapper}>
        <Nom035SummaryChart
          
          metric="global"
          title="Riesgo global"
          filterConfigs={filterConfigs}
          rangeLabels={globalRangeLabels}
        />
        <Nom035SummaryChart
          
          clinical
          title="Requiere atención médica"
          filterConfigs={filterConfigs}
        />
      </div>
    </section>

    <hr className={styles.separator} />

    {/* Por categoría y sus dominios */}
    {categoryKeys.map((catKey, idx) => {
      const domains = Object.keys(nom035Ranges.categoryDomains[catKey].domains);
      return (
        <React.Fragment key={catKey}>
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>{categoryLabels[catKey]}</h3>

            {/* Caso: 1 dominio → 2 gráficas lado a lado */}
            {domains.length === 1 && (
              <div className={styles.twoCols}>
                <Nom035SummaryChart
                  
                  metric={catKey}
                  title="General"
                  filterConfigs={filterConfigs}
                  rangeLabels={categoryRangeLabels[catKey]}
                />
                <Nom035SummaryChart
                  
                  metric={domains[0]}
                  title={domainLabels[domains[0]]}
                  filterConfigs={filterConfigs}
                  rangeLabels={domainRangeLabels[domains[0]]}
                />
              </div>
            )}

            {/* Caso: 2 dominios → General full width + 2 mitades */}
            {domains.length === 2 && (
              <>
                <div className={styles.fullWidth}>
                  <Nom035SummaryChart
                    
                    metric={catKey}
                    title="General"
                    filterConfigs={filterConfigs}
                    rangeLabels={categoryRangeLabels[catKey]}
                  />
                </div>
                <div className={styles.twoCols}>
                  {domains.map(domKey => (
                    <Nom035SummaryChart
                      key={domKey}
                      
                      metric={domKey}
                      title={domainLabels[domKey]}
                      filterConfigs={filterConfigs}
                      rangeLabels={domainRangeLabels[domKey]}
                    />
                  ))}
                </div>
              </>
            )}

            {/* Caso: 3 dominios → General full, 2 mitades, último full */}
            {domains.length === 3 && (
              <>
                <div className={styles.fullWidth}>
                  <Nom035SummaryChart
                    
                    metric={catKey}
                    title="General"
                    filterConfigs={filterConfigs}
                    rangeLabels={categoryRangeLabels[catKey]}
                  />
                </div>
                <div className={styles.twoCols}>
                  {[domains[0], domains[1]].map(domKey => (
                    <Nom035SummaryChart
                      key={domKey}
                      
                      metric={domKey}
                      title={domainLabels[domKey]}
                      filterConfigs={filterConfigs}
                      rangeLabels={domainRangeLabels[domKey]}
                    />
                  ))}
                </div>
                <div className={styles.fullWidth}>
                  <Nom035SummaryChart
                    
                    metric={domains[2]}
                    title={domainLabels[domains[2]]}
                    filterConfigs={filterConfigs}
                    rangeLabels={domainRangeLabels[domains[2]]}
                  />
                </div>
              </>
            )}
          </section>
          {idx < categoryKeys.length - 1 && <hr className={styles.separator} />}
        </React.Fragment>
      );
    })}

    <hr className={styles.separator} />

    {/* Dominios independientes */}
  </div>
);
}
